import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Checkbox
} from '@mui/material';
import { CompositionRole, ISalaryComponent } from '../../interfaces/payroll.interface';
import { usePermission } from '../../hooks/usePermission';
import { helpWhy } from '../../help/why';
import { PERMISSIONS } from '../../constants/permissions';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: Partial<ISalaryComponent>) => void;
    component: ISalaryComponent | null;
}

const SalaryComponentForm: React.FC<Props> = ({ open, onClose, onSave, component }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<'EARNING' | 'DEDUCTION'>('EARNING');
    const [isActive, setIsActive] = useState(true);
    const [isOvertimeBase, setIsOvertimeBase] = useState(false);
    const [isOvertimeEarning, setIsOvertimeEarning] = useState(false);
    const [isBonusEarning, setIsBonusEarning] = useState(false);
    // T3ab fix round 1 / H3 + folded T2c: the designation write path (EARNING guard +
    // single-holder swap) now lives in SalaryComponentService; this checkbox is the only place
    // that can actually set it -- following the exact idiom of the three siblings above.
    const [isPieceRateEarning, setIsPieceRateEarning] = useState(false);
    const [compositionRole, setCompositionRole] = useState<CompositionRole>('NONE');
    const canManage = usePermission(PERMISSIONS.SALARY_COMPONENT_MANAGE);

    useEffect(() => {
        if (component) {
            setName(component.name);
            setType(component.type);
            setIsActive(component.isActive);
            setIsOvertimeBase(!!component.isOvertimeBase);
            setIsOvertimeEarning(!!component.isOvertimeEarning);
            setIsBonusEarning(!!component.isBonusEarning);
            setIsPieceRateEarning(!!component.isPieceRateEarning);
            setCompositionRole(component.compositionRole ?? 'NONE');
        } else {
            setName('');
            setType('EARNING');
            setIsActive(true);
            setIsOvertimeBase(false);
            setIsOvertimeEarning(false);
            setIsBonusEarning(false);
            setIsPieceRateEarning(false);
            setCompositionRole('NONE');
        }
    }, [component, open]);

    const handleTypeChange = (newType: 'EARNING' | 'DEDUCTION') => {
        setType(newType);
        if (newType === 'DEDUCTION') {
            setIsOvertimeBase(false);
            setIsOvertimeEarning(false);
            setIsBonusEarning(false);
            setIsPieceRateEarning(false);
            setCompositionRole('NONE');
        }
    };

    const handleSubmit = () => {
        onSave({ name, type, isActive, isOvertimeBase, isOvertimeEarning, isBonusEarning, isPieceRateEarning, compositionRole });
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.componentForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>{component ? 'Edit' : 'Add'} Salary Component</DialogTitle>
            <DialogContent data-help-section="payroll.componentForm.section.details">
                <TextField
                    autoFocus
                    margin="dense"
                    data-help="payroll.componentForm.field.name" label="Component Name"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={{ mb: 2, mt: 1 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select data-help="payroll.componentForm.field.type" value={type} label="Type" onChange={(e) => handleTypeChange(e.target.value as 'EARNING' | 'DEDUCTION')}>
                        <MenuItem value="EARNING">Earning</MenuItem>
                        <MenuItem value="DEDUCTION">Deduction</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    data-help="payroll.componentForm.field.active" label="Active"
                    sx={{ display: 'block', mb: type === 'EARNING' ? 1 : 0 }}
                />

                {type === 'EARNING' && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isOvertimeBase}
                                    onChange={(e) => setIsOvertimeBase(e.target.checked)}
                                />
                            }
                            data-help="payroll.componentForm.field.otBase" label="Use as overtime base (the basic wage the hourly rate is derived from)"
                            sx={{ display: 'block' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isOvertimeEarning}
                                    onChange={(e) => setIsOvertimeEarning(e.target.checked)}
                                />
                            }
                            data-help="payroll.componentForm.field.otEarning" label="Pay computed overtime into this component"
                            sx={{ display: 'block' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isBonusEarning}
                                    onChange={(e) => setIsBonusEarning(e.target.checked)}
                                />
                            }
                            data-help="payroll.componentForm.field.bonusEarning" label="Pay festival and attendance bonuses into this component (taxed)"
                            sx={{ display: 'block' }}
                        />
                        {canManage && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPieceRateEarning}
                                        onChange={(e) => setIsPieceRateEarning(e.target.checked)}
                                    />
                                }
                                data-help="payroll.componentForm.field.pieceRate" label="Pay piece-rate earnings into this component"
                                sx={{ display: 'block' }}
                            />
                        )}
                        {canManage && (
                            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                                <InputLabel id="composition-role-label">Wage sheet column</InputLabel>
                                <Select
                                    data-help="payroll.componentForm.field.wageColumn" labelId="composition-role-label"
                                    label="Wage sheet column"
                                    value={compositionRole}
                                    onChange={(e) => setCompositionRole(e.target.value as CompositionRole)}
                                >
                                    <MenuItem value="NONE">Other earnings</MenuItem>
                                    <MenuItem value="HOUSE_RENT">House rent</MenuItem>
                                    <MenuItem value="MEDICAL">Medical allowance</MenuItem>
                                    <MenuItem value="CONVEYANCE">Conveyance</MenuItem>
                                    <MenuItem value="FOOD">Food allowance</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.componentForm.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="payroll.componentForm.save" {...helpWhy(!name.trim() ? { kind: 'status', message: 'Type a component name first.' } : null)} onClick={handleSubmit} variant="contained" disabled={!name.trim()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SalaryComponentForm;
